import { Controller } from "@hotwired/stimulus";

// The mobile dock's two slide-up surfaces (below `lg`):
//
//   panel  the sub-nav Menu panel (a page's :sidebar_left, or its mobile
//          variant), closed by any click inside it (a link was chosen);
//   sheet  the form builder's assistant bottom sheet (TASK-268.4,
//          decision-78): a thread you read and a composer you type in, so
//          clicks inside it never close it.
//
// Only one is open at a time; the backdrop and Esc close whichever it is.
//
// The sheet also keeps the dock item's unread badge: the number of assistant
// replies newer than the newest one the member had in front of them the last
// time the sheet was open, remembered per form in localStorage (the reply
// times come from the server, `data-reply-at` on each finished reply, so the
// browser's clock never enters into it). The thread is replaced whole by Turbo
// Streams while a reply is written, so a MutationObserver recounts on every
// change; while the sheet is open, whatever arrives counts as seen.
//
// The sheet sits above the keyboard: on a phone the on-screen keyboard
// shrinks the visual viewport but not the layout viewport, so the sheet is
// lifted by the difference and capped to the room that is left.
export default class extends Controller {
  static targets = ["panel", "backdrop", "sheet", "badge"];
  static values = { seenKey: String };

  connect() {
    if (!this.hasSheetTarget) return;

    this.observer = new MutationObserver(() => this.refreshBadge());
    this.observer.observe(this.sheetTarget, { childList: true, subtree: true });
    this.onViewport = () => this.fitSheet();
    window.visualViewport?.addEventListener("resize", this.onViewport);
    window.visualViewport?.addEventListener("scroll", this.onViewport);
    this.refreshBadge();
  }

  disconnect() {
    this.observer?.disconnect();
    window.visualViewport?.removeEventListener("resize", this.onViewport);
    window.visualViewport?.removeEventListener("scroll", this.onViewport);
  }

  // ── The Menu panel ────────────────────────────────────────────────────

  open() {
    this.closeSheet();
    this.panelTarget.classList.remove("translate-y-full");
    this.backdropTarget.dataset.active = "";
  }

  // The panel closes on any click inside it (a nav link was chosen).
  close() {
    if (this.hasPanelTarget) this.panelTarget.classList.add("translate-y-full");
    this.closeSheet();
    delete this.backdropTarget.dataset.active;
  }

  toggle() {
    if (this.hasPanelTarget && this.panelTarget.classList.contains("translate-y-full")) {
      this.open();
    } else {
      this.close();
    }
  }

  // ── The assistant sheet ───────────────────────────────────────────────

  toggleSheet() {
    this.sheetOpen ? this.close() : this.openSheet();
  }

  openSheet() {
    if (!this.hasSheetTarget) return;

    if (this.hasPanelTarget) this.panelTarget.classList.add("translate-y-full");
    window.clearTimeout(this.hideTimer);
    this.sheetTarget.classList.remove("invisible");
    // One frame so the browser sees the sheet laid out before it slides.
    this.sheetTarget.setAttribute("aria-hidden", "false");
    window.cancelAnimationFrame(this.slideFrame);
    this.slideFrame = requestAnimationFrame(() => {
      if (this.sheetOpen) this.sheetTarget.classList.remove("translate-y-full");
    });
    this.backdropTarget.dataset.active = "";
    this.markSeen();
    this.refreshBadge();
    this.fitSheet();
  }

  closeSheet() {
    if (!this.hasSheetTarget || !this.sheetOpen) return;

    window.cancelAnimationFrame(this.slideFrame);
    this.sheetTarget.classList.add("translate-y-full");
    this.sheetTarget.setAttribute("aria-hidden", "true");
    this.sheetTarget.style.bottom = "";
    this.sheetTarget.style.maxHeight = "";
    // Out of the tab order once it has slid away.
    this.hideTimer = window.setTimeout(() => this.sheetTarget.classList.add("invisible"), 300);
    this.refreshBadge();
  }

  get sheetOpen() {
    // The attribute is the state (set at once); the transform class lags a frame.
    return this.hasSheetTarget && this.sheetTarget.getAttribute("aria-hidden") === "false";
  }

  fitSheet() {
    if (!this.sheetOpen) return;

    const viewport = window.visualViewport;
    if (!viewport) return;

    const keyboard = window.innerHeight - viewport.height - viewport.offsetTop;
    if (keyboard > 80) {
      this.sheetTarget.style.bottom = `${keyboard}px`;
      this.sheetTarget.style.maxHeight = `${viewport.height - 16}px`;
    } else {
      this.sheetTarget.style.bottom = "";
      this.sheetTarget.style.maxHeight = "";
    }
  }

  // ── The unread badge ──────────────────────────────────────────────────

  replyTimes() {
    return Array.from(this.sheetTarget.querySelectorAll("[data-reply-at]"))
      .map((element) => Number(element.dataset.replyAt))
      .filter((time) => Number.isFinite(time));
  }

  seenAt() {
    try {
      return Number(window.localStorage.getItem(this.seenKeyValue)) || 0;
    } catch (_error) {
      return 0;
    }
  }

  markSeen() {
    const newest = Math.max(0, ...this.replyTimes());
    if (newest <= this.seenAt()) return;

    try {
      window.localStorage.setItem(this.seenKeyValue, String(newest));
    } catch (_error) {
      // Private window or blocked storage: the badge just starts again.
    }
  }

  refreshBadge() {
    if (!this.hasBadgeTarget) return;

    if (this.sheetOpen) this.markSeen();
    const seen = this.seenAt();
    const unread = this.sheetOpen ? 0 : this.replyTimes().filter((time) => time > seen).length;
    this.badgeTarget.textContent = unread > 0 ? String(unread) : "";
    this.badgeTarget.classList.toggle("hidden", unread === 0);
    this.badgeTarget.classList.toggle("inline-flex", unread > 0);
  }
}
